/**
 * What a cue from beat 3 *means*, in a scene made of triangles.
 *
 * Same seam as `bornDirector.ts` and `latchDirector.ts`: `story/act1/otto.ts`
 * says "he demonstrates", and this file is the only thing that knows Otto has a
 * `roll` pivot at his centre of mass, a `breath` node whose origin is the floor,
 * and a head that can be pointed at a pen across the alley.
 *
 * ── Everything in here is presentation, and that is not an accident ──────────
 *
 * Beat 2's director had to keep one piece of simulation state (the gate's open
 * fraction) because a collider read it. **Nothing in beat 3 does.** There are no
 * watchers (spec §5.4, and the beat's own header says why), Otto has no collider
 * that moves, and the roll he performs is nine seconds of animation that nothing
 * else in the game can see. So this whole file runs on the render step, and if a
 * later session finds itself wanting `stepOtto(dt)` in `fixedUpdate`, the thing
 * to check first is whether something has quietly started reading him.
 *
 * ── The demonstration, and why it is a hand-written timeline ─────────────────
 *
 * It is not a clip in `src/anim/`. Clips there are `Pose` deltas addressed by
 * name against Rosalie's rig (D-015), driven by gait and speed, and reusable —
 * everything this is not. This happens once, in one beat, to one character, and
 * the shape of it *is* the writing: how long he hesitates before he commits, how
 * fast he goes over, how long he lies there. Putting that behind a clip API would
 * hide the only numbers in the file worth arguing about.
 */

import * as THREE from 'three';
import { OTTO_CUES } from '@/story/act1/otto';
import { findOttoParts } from '@/models/otto';
import type { Cue } from '@/story/types';
import type { OttoParts } from '@/models/otto';

/**
 * The demonstration, as a timeline in seconds.
 *
 * Read down the left column: he takes a second and a half to decide, three
 * seconds to go over, lies there for two, and needs two and a half to get back
 * up. **Getting up takes longer than going over**, and that asymmetry is the
 * entire characterisation — falling is easy at his age and standing is not.
 *
 * The total is 8.6 against the beat's nine-second hold, so he is on his feet and
 * still for four tenths of a second before the stage ends. That gap is
 * deliberate: a stage that changes at the exact instant an animation stops reads
 * as the animation *causing* the change, and the look across the alley is
 * supposed to be his idea.
 */
const DEMO = {
  /** Weight shifts, shoulder drops. Nothing has happened yet. */
  gather: 1.5,
  /** Over he goes. Slow — this is 300 kg of old plush, not a dog. */
  overTo: 4.5,
  /** On his back, legs up, not moving much. */
  restTo: 6.5,
  /** Back down, and the thump lands at the *start* of this, not the end. */
  downTo: 7.6,
  /** Standing again. */
  upTo: 8.6,
} as const;

/**
 * How far over he actually gets — and this number is staging, not animation.
 *
 * It was 2.35 rad (135°) and 135° was invisible. **The pen wall is 1.05 m and he
 * rolls behind it.** His legs are the only part of him that goes *up* during a
 * roll, and the arithmetic decides whether the beat's most expensive nine
 * seconds happen on screen or behind a slab of concrete: his hooves sit about
 * 0.55 m from the roll pivot, which is at 0.60, so at 135° they reach 0.99 m and
 * at 157° they reach 1.11 m. One of those clears the rail and the other does not.
 *
 * Which is also why he goes over *toward the alley* rather than into the pen —
 * see the sign on `roll.rotation.z`. D-071 says she cannot see over that wall,
 * so what the player gets is four hooves and a thump rather than a clean view of
 * the whole animal. That is the honest shot and it is the better one: she is
 * being shown something she can only half see, by someone who cannot really do
 * it any more.
 */
const ROLL_MAX = 2.75;

/**
 * How far he sinks while he is over.
 *
 * A body on its back sits lower than a body on its feet, and without this he
 * rotates about a fixed axis like a rotisserie. Deliberately small: every
 * centimetre here is a centimetre off the height his hooves reach, and that
 * height is what decides whether the roll clears the pen wall at all. See
 * `ROLL_MAX`.
 */
const ROLL_SINK = 0.04;

/** How hard he breathes, as a fraction of his height, and how fast. */
const BREATH = { rest: 0.012, effort: 0.038, restRate: 0.55, effortRate: 1.9 } as const;

/** How far his neck will actually turn. Beyond this he would need to move his feet. */
const HEAD_YAW_LIMIT = 1.15;

/**
 * How fast the pen lamp comes up and goes down, as an exponential rate.
 *
 * 1.1 is a little under two seconds to most of the way, which is slower than any
 * real lamp and is the point: a light that snaps on reads as a trigger volume and
 * a light that fades up reads as somebody having switched it. The player is
 * supposed to notice the room changing, not notice the game noticing them.
 */
const LAMP_RATE = 1.1;

/** Everything he can be looking at. See `OttoDirector.#lookingAt`. */
export type Attention = 'none' | 'her' | 'pen' | 'gap' | 'vent';

/** `window.__rosalie.ottoDebug`, routed from `diagnostics()`. */
export interface OttoDebug {
  /** 0..1 through the demonstration, or null when he is not doing it. */
  readonly demoProgress: number | null;
  /** What he is looking at. Half of what this beat actually says. */
  readonly attention: Attention;
  /**
   * The pen lamp's intensity, read off the light. The other half.
   *
   * Both of these used to be decoration a spec could read and nothing else;
   * now the beat's two silent stages are pointed at by exactly these two
   * values, so a spec that watches them is watching the beat's whole
   * instruction set. See D-087.
   */
  readonly penLamp: number;
  /** The far high-bay. Dark all beat, and lit by its last stage. */
  readonly farLamp: number;
  /** Metres she has rolled inside the hospital pen. */
  readonly rolledInStraw: number;
  /** How many she needs. Exposed so a spec asserts against the game's number. */
  readonly rollTarget: number;
  readonly fade: number;
  readonly ended: boolean;
}

export interface OttoDirectorParts {
  /** The group returned by `createOtto()`. Its rig is resolved here. */
  otto: THREE.Object3D;
  /** Where the straw pen is, world XZ. He looks at it, and that is the beat. */
  strawPen: { x: number; z: number };
  /**
   * The broken corner of his own pen mouth, world XZ — the way in.
   *
   * He looks at it once, and that look is the entire invitation. D-087 settled
   * that this beat says what it has to say with a light and a look after a
   * version that said nothing at all was played and reported as goalless, and he
   * is the only thing in this building that can point at something.
   */
  gap: { x: number; z: number };
  /**
   * The inside of **his** pen, from `penBounds(OTTO_PEN)`.
   *
   * Handed over as the room's own rectangle rather than re-derived, for the
   * reason `main.ts` gives where it takes the straw pen's: two lists built from
   * the same constants a few hundred lines apart is exactly the trap D-056 and
   * D-059 both recorded.
   *
   * It is used for one thing — see `#lookingAt`'s `'gap'` case — and it is the
   * only test in this file that could not be a distance. She can stand 1.05 m
   * from his centre-line without being in the pen at all, because the alley runs
   * right along his mouth; only the rectangle knows the difference.
   */
  hisPen: { minX: number; maxX: number; minZ: number; maxZ: number };
  /**
   * Where the vent in the far wall is, world XZ.
   *
   * He looks at this once, at the end, and it is the only instruction the beat's
   * last stage gives. See `OTTO_CUES.enough` in `handle`.
   */
  vent: { x: number; z: number };
  /**
   * The warm lamp over the empty straw pen — `INSPECTION_LAMP_NAME` in
   * `fattening.ts`.
   *
   * The director takes it dark at construction and gives it back on `dispose`,
   * so the hall as authored is unchanged for every other consumer of the room
   * (`?beat=0`, the turnarounds, the budget spec). A beat that permanently edits
   * a room's lighting design is a beat that has moved into the room's file.
   */
  penLamp: THREE.Light;
  /**
   * The fourth high-bay, at the far end — `FAR_LAMP_NAME` in `fattening.ts`.
   *
   * Dark for the whole beat and lit by its last stage. This is the beat's only
   * pointer at the vent that the player can actually see from the alley; the
   * reasoning is in `FAR_LAMP_NAME`'s own comment and the short version is that
   * both of the other candidates — Otto's head, and light at the vent itself —
   * are behind something.
   */
  farLamp: THREE.Light;
  /**
   * What a lit high-bay delivers — `HIGH_BAY_INTENSITY`, handed in.
   *
   * **The far lamp is authored DARK**, unlike the pen lamp, so its lit value
   * cannot be read off the light at construction the way `#lampFull` is. The
   * first version inverted this — authored the fitting at full intensity and had
   * this constructor switch it off — which meant every load of the room that did
   * not run beat 3 got a brightly lit far end. The room owns its own default and
   * the beat asks for the number; that is the direction that fails safe.
   */
  farLampLit: number;
}

export interface OttoDirectorOptions {
  /**
   * The thump. Called once, at the moment his back hits the bedding.
   *
   * Given a world position rather than played here, for the reason every other
   * director gives: this file owns *when*, `gameAudio.ts` owns *what*.
   */
  onThump(at: { x: number; y: number; z: number }): void;
  /** Called once, when he first notices her. A short, low, pleased sound. */
  onGreet?(at: { x: number; y: number; z: number }): void;
}

export class OttoDirector {
  readonly #parts: OttoDirectorParts;
  readonly #rig: OttoParts;
  readonly #options: OttoDirectorOptions;

  /** Seconds since `demonstrate`, or null when he is not doing it. */
  #demoTime: number | null = null;
  #thumped = false;
  #ended = false;

  /** Breath phase, kept here so it never resets when a cue arrives. */
  #breathPhase = 0;

  /**
   * Where the roll pivot sits when he is upright, read from the rig at
   * construction rather than hard-coded.
   *
   * `ROLL_Y` moved twice in `otto.ts` while he was being rebuilt. A copy of it
   * here would have dropped him through the concrete each time, silently and
   * only during the one stage that touches it.
   */
  readonly #restRollY: number;

  /**
   * His body — the node the hall places and turns — as distinct from
   * `parts.otto`, the `otto-pen` group `createOtto()` returns, which sits at
   * the origin unrotated. Every look and both sounds used to read the group's
   * transform: his head was aimed as if he stood at (0, 0) facing +Z, which
   * for her meant the neck limit and his snout on her back, and the greet and
   * the thump played 2.2 m from him (session 72). See `#frame`.
   */
  readonly #body: THREE.Object3D;

  /**
   * What his head is currently pointing at, and what it is being asked to point
   * at — both in his own group's local yaw.
   *
   * Two values rather than one because the head is *eased* toward the target
   * every frame. A head that snapped to its target would make an enormous old
   * animal read as a security camera, and he is the one character in the game
   * whose slowness is the characterisation.
   */
  #headYaw = 0;
  #headYawTarget = 0;

  /** Where he is looking: at her, at the pen, at the vent, or at nothing. */
  #attention: Attention = 'none';
  #greeted = false;

  /**
   * The pen lamp's authored intensity, and where it is being taken.
   *
   * Read off the light rather than authored here, for the same reason
   * `#restRollY` is read off the rig: the number lives in `fattening.ts`, it has
   * already been retuned once, and a copy of it here would quietly become a
   * second lighting design that only exists during beat 3.
   */
  readonly #lampFull: number;
  #lampTarget = 0;
  readonly #farFull: number;
  #farTarget = 0;

  constructor(parts: OttoDirectorParts, options: OttoDirectorOptions) {
    this.#parts = parts;
    this.#rig = findOttoParts(parts.otto);
    this.#restRollY = this.#rig.roll.position.y;
    this.#body = this.#rig.roll.parent ?? parts.otto;
    this.#options = options;

    // Dark from the first frame of the beat. She walks into a hall of three
    // sodium pools with one pen that has no light in it at all — which is what
    // makes the lamp coming up later an event rather than a brightness change.
    this.#lampFull = parts.penLamp.intensity;
    parts.penLamp.intensity = 0;

    // The far end is already dark — `fatteningFixtures` authors it at 0 — so
    // there is nothing to switch off here, only a value to remember for when the
    // last stage asks for it. The hall spends the whole beat teaching that a lit
    // bay is somewhere you can be, and then lights the one at the end.
    this.#farFull = parts.farLampLit;
  }

  /** True once the closing cue has fired. */
  get ended(): boolean {
    return this.#ended;
  }

  /** Where he is in the demonstration, 0..1, or null when he is not doing it. Harness only. */
  get demoProgress(): number | null {
    return this.#demoTime === null ? null : Math.min(1, this.#demoTime / DEMO.upTo);
  }

  /** What he is looking at. Harness only — it is the one thing the beat *says*. */
  get attention(): Attention {
    return this.#attention;
  }

  /** The pen lamp's current intensity. Harness only; the other half of what the beat says. */
  get penLampIntensity(): number {
    return this.#parts.penLamp.intensity;
  }

  handle(cue: Cue): void {
    switch (cue.id) {
      case OTTO_CUES.noticesHer:
        this.#attention = 'her';
        break;
      // He turns to the broken corner of his own gate. It is the invitation and
      // there is no other one — no prompt, no highlight, no text.
      //
      // **It does not stay pointed at a hole.** `#lookingAt` gives her back the
      // moment she is actually in the pen: an animal staring at a doorway while
      // the visitor stands at his cheek is a prop running a script, and the one
      // thing this beat cannot afford to make him look like is a prop.
      case OTTO_CUES.showsTheGap:
        this.#attention = 'gap';
        break;
      case OTTO_CUES.demonstrate:
        this.#demoTime = 0;
        this.#thumped = false;
        // He stops watching her while he does it. A head that stayed locked on
        // her through a log-roll would be a turret on a barrel, and the point of
        // the demonstration is that it takes everything he has.
        this.#attention = 'none';
        break;
      case OTTO_CUES.looksAcross:
        this.#attention = 'pen';
        break;
      // ── The two cues that used to do nothing at all ──────────────────────────
      //
      // `hersNow` and `enough` were emitted by the beat, arrived here, and fell
      // through to `default`. Beat 3's own header calls the straw *"a light and a
      // look, not a UI"* — the look existed and **the light never did**, and the
      // stage after it had no pointer of any kind. So the beat's last four
      // minutes were an enormous animal looking vaguely across an alley followed
      // by ninety seconds of nothing, twice. Alex, playing it: *"I'm not sure
      // what happens in beat 3, I can see Otto and walk around, is there any
      // goal?"* That is not a player missing something. That is the beat not
      // saying anything. D-087.
      //
      // The straw is hers, and now the room says so: the only warm light in the
      // building comes up over the one empty pen, while the only other animal in
      // the hall is looking at it. No prompt, no highlight, no text.
      case OTTO_CUES.hersNow:
        this.#lampTarget = this.#lampFull;
        break;
      // She has had the pen. The lamp goes back out and he turns to the vent —
      // **not to her**, which is what this used to do and which pointed the one
      // instrument the beat has at the player instead of at the way out. What is
      // left in the hall is a closing pool of warm light and 300 kg of pig
      // looking at a hole in the far wall.
      case OTTO_CUES.enough:
        this.#lampTarget = 0;
        this.#farTarget = this.#farFull;
        this.#attention = 'vent';
        break;
      case OTTO_CUES.end:
        this.#ended = true;
        break;
      default:
        // Unknown cues are ignored rather than thrown on, exactly as in the
        // other two directors: a beat that emits something this file has not
        // learned yet should not take the render step down.
        break;
    }
  }

  /**
   * Runs on the render step. Presentation only — see the file header.
   *
   * `her` is passed in rather than reached for, because a director that knows
   * how to find the player is a director that will eventually be asked to move
   * her.
   */
  update(dt: number, her: { x: number; y: number; z: number }): void {
    const frameDt = Number.isFinite(dt) ? Math.max(0, Math.min(0.1, dt)) : 0;

    const effort = this.#stepDemonstration(frameDt);
    this.#stepBreath(frameDt, effort);
    this.#stepHead(frameDt, her);
    this.#stepPenLamp(frameDt);
  }

  dispose(): void {
    // The lamp goes back to what `fattening.ts` authored. The room outlives the
    // beat — `?beat=0`, the turnarounds and the budget spec all load it — and a
    // director that took a light out and did not put it back would leave the
    // hospital pen dark in every one of them.
    this.#parts.penLamp.intensity = this.#lampFull;
    // Back to 0, which is what the room authors — NOT to `#farFull`, which is
    // what a lit one delivers. Restoring the lit value here would leave the far
    // end burning after the beat had finished with it.
    this.#parts.farLamp.intensity = 0;
  }

  /** The far high-bay's current intensity. Harness only, same reason as `penLampIntensity`. */
  get farLampIntensity(): number {
    return this.#parts.farLamp.intensity;
  }

  /** Advance the roll. Returns 0..1, how hard he is working right now. */
  #stepDemonstration(dt: number): number {
    if (this.#demoTime === null) return 0;
    this.#demoTime += dt;
    const t = this.#demoTime;

    let over = 0;
    let effort = 0;
    if (t < DEMO.gather) {
      // Nothing rotates yet. He leans, which is the shoulder taking the weight,
      // and it is the only warning the player gets that this is about to cost
      // him something.
      over = 0;
      effort = smoothstep(0, DEMO.gather, t) * 0.5;
    } else if (t < DEMO.overTo) {
      // Eased in, NOT eased out. He commits and then gravity has him — a
      // symmetric ease would make him look like he chose the moment he landed,
      // and the whole read is that he did not.
      const k = (t - DEMO.gather) / (DEMO.overTo - DEMO.gather);
      over = k * k * (3 - 2 * k) * 0.86 + k * k * k * 0.14;
      effort = 1;
    } else if (t < DEMO.restTo) {
      over = 1;
      effort = 0.25;
    } else if (t < DEMO.downTo) {
      // Back down, and faster than he went over. He is not lowering himself; he
      // is falling off his own back.
      const k = (t - DEMO.restTo) / (DEMO.downTo - DEMO.restTo);
      over = 1 - k * k;
      effort = 0.8;
    } else if (t < DEMO.upTo) {
      const k = (t - DEMO.downTo) / (DEMO.upTo - DEMO.downTo);
      over = 0;
      // Getting up is the hardest thing he does in the beat, and the effort
      // curve says so after the rotation has already finished.
      effort = 1 - k * k * (3 - 2 * k);
    } else {
      this.#demoTime = null;
      this.#rig.roll.rotation.z = 0;
      this.#rig.roll.position.y = this.#restRollY;
      return 0;
    }

    // Negative, so he goes over toward the alley — toward her and toward the
    // camera, rather than away into the pen wall where nothing would be visible.
    this.#rig.roll.rotation.z = -over * ROLL_MAX;

    // He sinks as he goes over and comes back up as he rights himself. Sine of
    // the rotation fraction rather than the fraction itself, so the lowest point
    // is when he is actually on his back and not on the way there.
    const sink = Math.sin(Math.min(1, over) * Math.PI * 0.5) * ROLL_SINK;
    this.#rig.roll.position.y = this.#restRollY - sink;

    // The thump is fired on the way DOWN, at the moment the rotation crosses
    // back through most of the way over — not at `over === 0`, which is when he
    // is already standing and half a second too late to be the sound of him
    // landing.
    if (!this.#thumped && t >= DEMO.restTo && over < 0.72) {
      this.#thumped = true;
      const at = this.#frame();
      this.#options.onThump({ x: at.x, y: 0.35, z: at.z });
    }

    return effort;
  }

  /**
   * Breath, as a Y scale on the node whose origin is the floor.
   *
   * It never stops, including while he is on his back, because a character who
   * stops breathing when the script is not looking at him is a prop. The rate
   * and depth both rise with effort, which is what makes the nine seconds read
   * as *cost* rather than as a manoeuvre.
   */
  #stepBreath(dt: number, effort: number): void {
    const rate = THREE.MathUtils.lerp(BREATH.restRate, BREATH.effortRate, effort);
    const depth = THREE.MathUtils.lerp(BREATH.rest, BREATH.effort, effort);
    this.#breathPhase = (this.#breathPhase + dt * rate) % 1;
    // Not a sine. An animal's breath is a quick draw and a long release, and the
    // asymmetry is most of what makes it read as breathing rather than as a
    // pulsing balloon.
    const cycle = Math.sin(this.#breathPhase * Math.PI * 2);
    const shaped = cycle > 0 ? cycle ** 0.7 : -((-cycle) ** 1.4);
    this.#rig.breath.scale.y = 1 + depth * shaped;
  }

  /**
   * Point his head, in his own local yaw, eased.
   *
   * The target is computed from world positions rather than authored as an
   * angle, because his group's own rotation is set by the hall and an authored
   * angle would silently become wrong the day somebody turns him round. That is
   * not hypothetical — which way he faces in his pen was still open while this
   * was being written.
   */
  #stepHead(dt: number, her: { x: number; y: number; z: number }): void {
    const target = this.#lookingAt(her);

    if (target === null) {
      this.#headYawTarget = 0;
    } else {
      const at = this.#frame();
      const world = Math.atan2(target.x - at.x, target.z - at.z);
      const local = wrapAngle(world - at.yaw);
      this.#headYawTarget = THREE.MathUtils.clamp(local, -HEAD_YAW_LIMIT, HEAD_YAW_LIMIT);
    }

    // Slower than anything else in the game turns. 1.6 is about a second to
    // cover a right angle, which is what an animal this size looks like.
    this.#headYaw += (this.#headYawTarget - this.#headYaw) * (1 - Math.exp(-1.6 * dt));
    this.#rig.head.rotation.y = this.#headYaw;

    if (!this.#greeted && this.#attention === 'her') {
      this.#greeted = true;
      const at = this.#frame();
      this.#options.onGreet?.({ x: at.x, y: 0.7, z: at.z });
    }
  }

  /**
   * His body's world XZ and the world yaw of his +Z, from its matrix, so the
   * frame is right however the hall nests or turns him. Updated here rather
   * than trusted from the last render, which has not happened on the first
   * frame.
   */
  #frame(): { x: number; z: number; yaw: number } {
    this.#body.updateWorldMatrix(true, false);
    const e = this.#body.matrixWorld.elements;
    return { x: e[12], z: e[14], yaw: Math.atan2(e[8], e[10]) };
  }

  /**
   * Where his head is being asked to point, in world XZ, or null for "nowhere".
   *
   * A `switch` over the whole union rather than a chain of ternaries, so adding a
   * fifth thing for him to look at is a compile error here instead of a silent
   * fall-through to a head that returns to centre.
   */
  #lookingAt(her: { x: number; z: number }): { x: number; z: number } | null {
    switch (this.#attention) {
      case 'her':
        return { x: her.x, z: her.z };
      case 'pen':
        return this.#parts.strawPen;
      // The way in, until she has taken it — then her, because he is looking at
      // the hole *for* her and the hole has stopped being the answer.
      //
      // The rectangle and not a radius: the alley runs along his mouth, so she
      // can stand about a metre from his centre-line while still being outside
      // his pen entirely, and a distance test would have him greeting people in
      // the corridor. See `OttoDirectorParts.hisPen`.
      case 'gap': {
        const pen = this.#parts.hisPen;
        const inside = her.x >= pen.minX && her.x <= pen.maxX && her.z >= pen.minZ && her.z <= pen.maxZ;
        return inside ? { x: her.x, z: her.z } : this.#parts.gap;
      }
      // The vent is twelve metres up the hall and well outside `HEAD_YAW_LIMIT`,
      // so what the player actually sees is his neck going as far as it goes and
      // stopping. That is the correct read and not a compromise: an animal that
      // wants to look at something it cannot turn far enough to see is exactly
      // what a stiff old boar looks like, and the direction is unambiguous.
      case 'vent':
        return this.#parts.vent;
      case 'none':
        return null;
    }
  }

  /**
   * The pen lamp, eased toward whatever the last cue asked for.
   *
   * Exponential rather than linear because both ends matter differently: coming
   * up it should feel like a filament warming, and going out it should trail
   * rather than click. See `LAMP_RATE`.
   */
  #stepPenLamp(dt: number): void {
    const k = 1 - Math.exp(-LAMP_RATE * dt);
    const pen = this.#parts.penLamp;
    pen.intensity += (this.#lampTarget - pen.intensity) * k;
    const far = this.#parts.farLamp;
    far.intensity += (this.#farTarget - far.intensity) * k;
  }
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/** Shortest signed representation of an angle, so a turn never goes the long way. */
function wrapAngle(a: number): number {
  return Math.atan2(Math.sin(a), Math.cos(a));
}
